<script setup lang="ts">
const props = withDefaults(defineProps<{
  to?: string | object
  href?: string
  active?: boolean
  disabled?: boolean
  prependIcon?: string
  appendIcon?: string
  density?: 'default' | 'comfortable' | 'compact'
  value?: any
  rounded?: boolean
  color?: string
  subtitle?: string
  title?: string
  lines?: 'one' | 'two' | 'three'
}>(), {
  density: 'default',
})

defineEmits<{ click: [e: MouseEvent] }>()

const NuxtLink = defineNuxtLink({})

const tag = computed(() => {
  if (props.to) return NuxtLink
  if (props.href) return 'a'
  return 'div'
})
</script>

<template>
  <component
    :is="tag"
    v-bind="to ? { to } : href ? { href } : {}"
    class="m-list-item"
    :class="[
      `m-list-item--${density}`,
      {
        'm-list-item--active': active,
        'm-list-item--disabled': disabled,
        'm-list-item--clickable': to || href || $attrs.onClick,
        'm-list-item--rounded': rounded,
        [`m-list-item--${color}`]: color,
      }
    ]"
    role="listitem"
    @click="$emit('click', $event)"
  >
    <div v-if="prependIcon || $slots.prepend" class="m-list-item__prepend">
      <slot name="prepend">
        <MIcon v-if="prependIcon" :icon="prependIcon" />
      </slot>
    </div>

    <div class="m-list-item__content">
      <div v-if="title || $slots.title" class="m-list-item__title">
        <slot name="title">{{ title }}</slot>
      </div>
      <slot />
      <div v-if="subtitle || $slots.subtitle" class="m-list-item__subtitle">
        <slot name="subtitle">{{ subtitle }}</slot>
      </div>
    </div>

    <div v-if="appendIcon || $slots.append" class="m-list-item__append">
      <slot name="append">
        <MIcon v-if="appendIcon" :icon="appendIcon" />
      </slot>
    </div>
  </component>
</template>

<style scoped>
.m-list-item {
  display: flex;
  align-items: center;
  gap: var(--m-space-3);
  text-decoration: none;
  color: var(--m-on-surface);
  transition: background var(--m-transition-fast);
}

.m-list-item--default { padding: var(--m-space-2) var(--m-space-4); min-height: 48px; }
.m-list-item--comfortable { padding: var(--m-space-2) var(--m-space-3); min-height: 40px; }
.m-list-item--compact { padding: var(--m-space-1) var(--m-space-3); min-height: 32px; font-size: var(--m-font-size-sm); }

.m-list-item--clickable { cursor: pointer; }
.m-list-item--clickable:hover { background: rgba(0, 0, 0, 0.04); }

.m-list-item--active { background: color-mix(in srgb, var(--m-primary) 12%, transparent); color: var(--m-primary); }
.m-list-item--active .m-list-item__prepend { color: var(--m-primary); }

.m-list-item--rounded { border-radius: var(--m-radius); }

.m-list-item--disabled { opacity: 0.5; pointer-events: none; }

.m-list-item--primary.m-list-item--active { color: var(--m-primary); }

.m-list-item__prepend,
.m-list-item__append {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  color: var(--m-on-surface-variant);
}

.m-list-item__content {
  flex: 1;
  min-width: 0;
}

.m-list-item__title {
  font-size: var(--m-font-size-sm);
  font-weight: 500;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.m-list-item__subtitle {
  font-size: var(--m-font-size-xs);
  color: var(--m-on-surface-variant);
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
